import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Lock, Trophy, Camera, Loader2, HelpCircle, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL, ARCHETYPES } from "../lib/constants";
import ColorCombo from "../components/ColorCombo";
import { BadgeRow, BadgeCard } from "../components/Badges";

const CJ_HUB_GREETINGS = [
  (n) => `Hey ${n}! Where are we banking impact today?`,
  (n) => `Welcome back, ${n}. Pick your lane.`,
  (n) => `${n}! Stories, impact, or kudos, your move.`,
  (n) => `Hi ${n}. Let's make today count.`,
  (n) => `Hey ${n}, the dashboard's warmed up for you.`,
];

const FALLBACK_ICON = LucideIcons.Sparkles;

export default function Hub() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tiles, setTiles] = useState([]);
  const avatarInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPoints, setShowPoints] = useState(false);

  useEffect(() => {
    api.get("/stats/me").then(({ data }) => setStats(data)).catch(() => setStats(null));
    api.get("/stats/leaderboard").then(({ data }) => setLeaderboard(data?.leaderboard || [])).catch(() => setLeaderboard([]));
    api.get("/hub/tiles").then(({ data }) => setTiles(Array.isArray(data) ? data : [])).catch(() => setTiles([]));
  }, []);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large", { description: "Max 8 MB." });
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profile photo updated!");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error("Couldn't update your photo", { description: detail });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  if (!user) return null;
  const firstName = (user.name || "there").split(" ")[0];
  const greeting = CJ_HUB_GREETINGS[new Date().getDate() % CJ_HUB_GREETINGS.length](firstName);
  const archetype = ARCHETYPES[user.archetype] || ARCHETYPES.blue;
  const points = stats?.totals?.points || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      {/* CJ greeting card */}
      <motion.div
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="tc-card p-5 sm:p-6 flex items-center gap-4 sm:gap-5 relative overflow-hidden"
        data-testid="hub-greeting"
        style={{ backgroundImage: "linear-gradient(135deg,#FFFFFF 0%,#FFF7EE 100%)" }}
      >
        <motion.img
          src={CJ_AVATAR_URL}
          alt="CJ"
          initial={{ scale: 0.7, rotate: -8 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 12, delay: 0.1 }}
          className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover shadow-md ring-4 ring-white shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="tc-eyebrow text-orange-600">It's CJ!</div>
          <h1 className="font-display text-xl sm:text-2xl text-tc-ink leading-tight mt-0.5" data-testid="hub-greeting-text">{greeting}</h1>
        </div>

        {/* Your avatar — clickable to change photo */}
        <div className="hidden sm:flex flex-col items-center shrink-0">
          <div className="relative group">
            <div
              className="w-14 h-14 rounded-full bg-cover bg-center border-2 shadow-sm"
              style={{ backgroundImage: `url(${avatarSrc(user)})`, borderColor: archetype.color }}
              data-testid="hub-user-avatar"
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              data-testid="hub-avatar-upload"
              title="Change your photo"
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center hover:scale-110 transition-transform"
            >
              {uploadingAvatar ? <Loader2 size={13} className="animate-spin text-slate-500" /> : <Camera size={13} className="text-slate-600" />}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarChange}
              className="hidden"
              data-testid="hub-avatar-input"
            />
          </div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mt-1.5">Your photo</div>
        </div>

        <button
          type="button"
          onClick={() => setShowPoints(true)}
          data-testid="hub-points-trigger"
          className="hidden sm:flex flex-col items-end shrink-0 pl-3 border-l border-orange-200/60 group hover:bg-orange-50/40 -my-1 py-1 px-2 rounded-xl transition-colors"
          title="Click to see how points are earned"
        >
          <div className="tc-eyebrow text-slate-400 flex items-center gap-1">Your points <HelpCircle size={11} className="text-orange-400 group-hover:text-orange-600" /></div>
          <div className="font-display text-2xl text-[#f68b1f] leading-none mt-1" data-testid="hub-points">{points}</div>
          <ColorCombo user={user} variant="compact" className="mt-1.5" />
        </button>
      </motion.div>

      <AnimatePresence>
        {showPoints && <PointsModal onClose={() => setShowPoints(false)} />}
      </AnimatePresence>

      {/* Two-column layout: tiles on the left, leaderboard on the right */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Tiles — compact icon grid: scales to 12+ modules without scrolling */}
        <div className="lg:col-span-2 flex flex-col gap-3" data-testid="hub-tiles-column">
          {/* Email Co-Pilot gets its own hero button — this is the most-used tool. */}
          <EmailCoPilotHero tile={tiles.find((t) => t.url === "/email-copilot")} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 content-start" data-testid="hub-tiles">
            {tiles.filter((t) => t.url !== "/email-copilot").map((t, idx) => (
              <Tile key={t.id || t.key} tile={t} delay={idx * 0.04} />
            ))}
          </div>
        </div>

        {/* Leaderboard sidebar */}
        {leaderboard.length > 0 && (
          <motion.section
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 140, damping: 18 }}
            className="rounded-2xl bg-white border-2 border-[#083050] p-5 self-start lg:sticky lg:top-24"
            data-testid="hub-leaderboard"
          >
            <div className="h-1.5 w-full -mt-5 -mx-5 mb-4 rounded-t-2xl" style={{ backgroundColor: "#083050" }} />
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-tc-ink flex items-center gap-2">
                <Trophy size={20} className="text-[#ffd400]" /> Leaderboard
              </h2>
              <Link to="/stories" className="text-[10px] font-bold uppercase tracking-widest text-[#f68b1f] hover:text-[#cc6f15]">All stories →</Link>
            </div>
            <button
              type="button"
              onClick={() => setShowPoints(true)}
              data-testid="hub-leaderboard-points-help"
              className="w-full mb-3 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#f68b1f] hover:text-white hover:bg-[#f68b1f] border-2 border-[#f68b1f] rounded-full py-1.5 transition-colors"
            >
              <HelpCircle size={13} /> How do points work?
              {/* build-marker-v2 */}
            </button>
            <ol className="space-y-1.5">
              {leaderboard.slice(0, 8).map((u, i) => {
                const isMe = u.id === user.id;
                // Top 3 use True Colors palette, the rest get a neutral navy-tinted chip
                const rankColor = i === 0 ? "#f68b1f" : i === 1 ? "#0091be" : i === 2 ? "#7ac143" : "#083050";
                return (
                  <li
                    key={u.id}
                    data-testid={`hub-leaderboard-row-${u.id}`}
                    className={`flex items-center gap-3 px-2.5 py-2 rounded-lg ${isMe ? "bg-orange-50" : "hover:bg-slate-50"}`}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold text-white shrink-0" style={{ background: rankColor }}>
                      {i + 1}
                    </div>
                    <div
                      className="w-10 h-10 rounded-full bg-cover bg-center bg-slate-200 shrink-0 border-2 border-white shadow-sm"
                      style={{ backgroundImage: `url(${avatarSrc(u)})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-bold text-tc-ink truncate leading-tight flex items-center gap-1.5">
                        <span className="truncate">{u.name.split(" ")[0]}</span>
                        {isMe && <span className="text-[9px] uppercase tracking-widest text-[#f68b1f] font-extrabold">You</span>}
                        {u.badges?.length > 0 && <BadgeRow keys={u.badges.slice(0, 3)} className="shrink-0" />}
                      </div>
                      <ColorCombo user={u} variant="compact" className="mt-0.5" />
                    </div>
                    <div className="font-display text-2xl leading-none shrink-0" style={{ color: isMe ? "#f68b1f" : "#083050" }}>{u.points || 0}</div>
                  </li>
                );
              })}
            </ol>
          </motion.section>
        )}

        {/* My badges card under the leaderboard */}
        <div className="lg:col-span-3">
          <BadgeCard />
        </div>
      </div>
    </div>
  );
}

const POINT_ROWS = [
  { what: "Submit a complete story", pts: "+100", group: "Stories you write" },
  { what: "Add measurable outcomes", pts: "+50", group: "Stories you write" },
  { what: "Add a client quote", pts: "+25", group: "Stories you write" },
  { what: "Add a photo or video", pts: "+25", group: "Stories you write" },
  { what: "Resubmit a sent-back story", pts: "+30", group: "Stories you write" },
  { what: "Client testimonial names you", pts: "+100", group: "Client testimonials" },
  { what: "Testimonial includes a quote", pts: "+25", group: "Client testimonials" },
  { what: "Testimonial includes photo/video", pts: "+25", group: "Client testimonials" },
  { what: "Receive a kudos from a teammate", pts: "+5", group: "Recognition" },
];

function PointsModal({ onClose }) {
  // group rows by section heading
  const groups = POINT_ROWS.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="points-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-tc-ink">How points work</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-lg leading-none">×</button>
        </div>
        <p className="text-xs text-slate-500 mt-1">Points roll into your total and the team leaderboard. Bigger, richer stories earn more.</p>

        <div className="mt-4 space-y-4">
          {Object.entries(groups).map(([group, rows]) => (
            <div key={group}>
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-orange-600 mb-1.5">{group}</div>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
                {rows.map((r) => (
                  <div key={r.what} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-tc-ink">{r.what}</span>
                    <span className="font-extrabold text-[#f68b1f]">{r.pts}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 rounded-xl bg-slate-50 text-[11px] text-slate-600 leading-snug">
          <div className="font-bold text-tc-ink mb-1">When do points hit my account?</div>
          Stories you submit → points lock in when your COO <span className="font-bold">approves</span> the story.<br />
          Client testimonials → points fire <span className="font-bold">immediately</span> on submit (the client showed up — you already earned it).<br />
          Kudos → points hit <span className="font-bold">instantly</span> when a teammate sends one your way.
        </div>
      </motion.div>
    </motion.div>
  );
}

function Tile({ tile, delay }) {
  const { title, blurb, color, available = true, eta, url, to, icon } = tile;
  // Resolve icon: API gives `icon` (string name), legacy used `Icon` (component).
  const Icon = tile.Icon || (icon ? LucideIcons[icon] : null) || FALLBACK_ICON;
  // Resolve href: API gives `url`, legacy used `to`.
  const href = url || to || "/";
  const isExternal = /^https?:\/\//i.test(href);
  const key = tile.id || tile.key || tile.title;

  // Use <a target="_blank"> for external links, <Link> for internal SPA routes.
  let Wrapper, wrapperProps;
  if (!available) {
    Wrapper = "div";
    wrapperProps = { "data-testid": `hub-tile-${key}-disabled`, title: blurb };
  } else if (isExternal) {
    Wrapper = "a";
    wrapperProps = { href, target: "_blank", rel: "noopener noreferrer", "data-testid": `hub-tile-${key}`, title: blurb };
  } else {
    Wrapper = Link;
    wrapperProps = { to: href, "data-testid": `hub-tile-${key}`, title: blurb };
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
    >
      <Wrapper
        {...wrapperProps}
        className={`group block relative overflow-hidden rounded-2xl bg-white border-2 transition-all ${available ? "hover:-translate-y-1 cursor-pointer hover:shadow-lg" : "opacity-75 cursor-default"}`}
        style={{ borderColor: color, boxShadow: "0 1px 2px rgba(8,48,80,0.06)" }}
      >
        <div className="h-1.5 w-full" style={{ backgroundColor: color }} />
        <div className="p-3 sm:p-3.5 flex items-center gap-3 min-h-[72px]">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
            style={{ backgroundColor: color, color: "#fff" }}
          >
            <Icon size={18} strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-sm sm:text-base leading-tight text-tc-ink line-clamp-2" data-testid={`hub-tile-title-${key}`}>{title}</h2>
            <p className="hidden sm:block text-[11px] leading-snug mt-0.5 text-slate-500 line-clamp-1 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-10 transition-all duration-200">
              {blurb}
            </p>
          </div>
          {!available && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5 shrink-0">
              <Lock size={9} /> {eta || "Soon"}
            </span>
          )}
          {available && (
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="text-slate-300 group-hover:text-current transition-colors shrink-0"
            >
              <ArrowRight size={14} style={{ color }} />
            </motion.span>
          )}
        </div>
      </Wrapper>
    </motion.div>
  );
}


/* ------------------------- Email Co-Pilot Hero -------------------------
 * CJ's email copilot is the most-used tool on the Hub, so it gets its own
 * oversized button above the regular tile grid. We still keep the backing
 * `hub_tiles` row (admin can rename/hide/etc), we just render it bigger.
 */
function EmailCoPilotHero({ tile }) {
  if (!tile) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08, type: "spring", stiffness: 150, damping: 20 }}
    >
      <Link
        to={tile.url || "/email-copilot"}
        data-testid="hub-email-copilot-hero"
        className="group relative block overflow-hidden rounded-3xl border-2 border-[#f68b1f] shadow-[0_10px_30px_-10px_rgba(246,139,31,0.55)] hover:shadow-[0_18px_42px_-10px_rgba(246,139,31,0.7)] transition-all"
        style={{
          background:
            "linear-gradient(120deg, #f68b1f 0%, #ff7a2a 55%, #ffb354 100%)",
        }}
      >
        {/* Decorative floating sparkles */}
        <motion.div
          className="absolute top-4 right-24 text-white/40"
          animate={{ y: [0, -6, 0], rotate: [0, 12, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles size={28} />
        </motion.div>
        <motion.div
          className="absolute bottom-5 right-10 text-white/30"
          animate={{ y: [0, -4, 0], rotate: [0, -8, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
        >
          <Sparkles size={18} />
        </motion.div>

        <div className="relative flex items-center gap-4 sm:gap-5 p-4 sm:p-6">
          {/* CJ avatar wrapped in a soft ring */}
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full bg-white/30 blur-xl" />
            <img
              src={CJ_AVATAR_URL}
              alt="CJ"
              className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white object-cover shadow-lg"
            />
          </div>

          {/* Label */}
          <div className="flex-1 min-w-0 text-white">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/25 backdrop-blur-sm text-[10px] sm:text-[11px] font-black uppercase tracking-widest mb-1.5">
              <Mail size={11} /> New
            </div>
            <div className="font-display text-2xl sm:text-3xl font-black leading-tight">
              Ask CJ to write an email
            </div>
            <div className="text-[13px] sm:text-sm text-white/90 mt-0.5">
              Cold outreach, replies, objections. In your voice. In seconds.
            </div>
          </div>

          {/* Arrow */}
          <motion.div
            className="hidden sm:flex shrink-0 w-11 h-11 rounded-full bg-white text-[#f68b1f] items-center justify-center shadow-md group-hover:translate-x-1 transition-transform"
            animate={{ x: [0, 3, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>
      </Link>
    </motion.div>
  );
}
